/**
 * Commission structure and tracking enums
 * @see Issue #238 - Partner Companies B2B Model
 * @see Issue #390 - Employee Commission Split
 */

/**
 * Method for calculating commission
 */
export enum CommissionCalculationMethod {
  PERCENTAGE = 'percentage', // % of loan amount
  FLAT_FEE = 'flat_fee', // Fixed dollar amount
  POINTS = 'points', // Points (1 point = 1%)
  BASIS_POINTS = 'basis_points', // Basis points (100 bps = 1%)
  TIERED = 'tiered', // Volume-based tiers
}

/**
 * Base amount for commission calculation
 */
export enum CommissionCalculationBase {
  LOAN_AMOUNT = 'loan_amount',
  FUNDED_AMOUNT = 'funded_amount',
  PURCHASE_PRICE = 'purchase_price',
  ARV = 'arv', // After Repair Value
}

/**
 * Type of volume tier
 */
export enum CommissionTierType {
  VOLUME_MONTHLY = 'volume_monthly',
  VOLUME_QUARTERLY = 'volume_quarterly',
  VOLUME_ANNUAL = 'volume_annual',
  DEAL_COUNT_MONTHLY = 'deal_count_monthly',
  DEAL_COUNT_QUARTERLY = 'deal_count_quarterly',
  DEAL_COUNT_ANNUAL = 'deal_count_annual',
}

/**
 * Type of bonus in agreement
 */
export enum AgreementBonusType {
  SIGNING = 'signing', // One-time on agreement signing
  MILESTONE = 'milestone', // On reaching volume milestone
  RETENTION = 'retention', // Annual retention bonus
  EXCLUSIVITY = 'exclusivity', // For exclusivity commitment
  FIRST_DEAL = 'first_deal', // First deal bonus
  PERFORMANCE = 'performance', // Quarterly/annual performance
}

/**
 * Status of a bonus
 */
export enum BonusStatus {
  PENDING = 'pending',
  EARNED = 'earned',
  PAID = 'paid',
  FORFEITED = 'forfeited',
}

/**
 * Type of commission split recipient
 */
export enum CommissionRecipientType {
  COMPANY = 'company',
  USER = 'user',
  BRANCH_OVERRIDE = 'branch_override',
}

/**
 * Type of internal split configuration
 */
export enum SplitType {
  PERCENTAGE = 'percentage',
  FLAT_FEE = 'flat_fee',
  TIERED = 'tiered',
}

/**
 * Status of internal commission
 */
export enum InternalCommissionStatus {
  CALCULATED = 'calculated',
  APPROVED = 'approved',
  PAID = 'paid',
  DISPUTED = 'disputed',
  VOIDED = 'voided',
}

/**
 * Status of commission receipt (partner receiving)
 */
export enum CommissionReceiptStatus {
  EXPECTED = 'expected', // Deal funded, awaiting payment
  RECEIVED = 'received', // Payment received
  DISPUTED = 'disputed', // Discrepancy identified
  ADJUSTED = 'adjusted', // Amount was adjusted
  WRITTEN_OFF = 'written_off', // Won't be received
}

/**
 * Status of commission split payment
 */
export enum CommissionSplitStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  PAID = 'paid',
  HELD = 'held', // On hold (compliance, etc.)
}

/**
 * Commission calculation method display names
 */
export const COMMISSION_CALCULATION_METHOD_NAMES: Record<CommissionCalculationMethod, string> = {
  [CommissionCalculationMethod.PERCENTAGE]: 'Percentage',
  [CommissionCalculationMethod.FLAT_FEE]: 'Flat Fee',
  [CommissionCalculationMethod.POINTS]: 'Points',
  [CommissionCalculationMethod.BASIS_POINTS]: 'Basis Points',
  [CommissionCalculationMethod.TIERED]: 'Tiered',
};

/**
 * Commission calculation base display names
 */
export const COMMISSION_CALCULATION_BASE_NAMES: Record<CommissionCalculationBase, string> = {
  [CommissionCalculationBase.LOAN_AMOUNT]: 'Loan Amount',
  [CommissionCalculationBase.FUNDED_AMOUNT]: 'Funded Amount',
  [CommissionCalculationBase.PURCHASE_PRICE]: 'Purchase Price',
  [CommissionCalculationBase.ARV]: 'After Repair Value (ARV)',
};

/**
 * Internal commission status display names
 */
export const INTERNAL_COMMISSION_STATUS_NAMES: Record<InternalCommissionStatus, string> = {
  [InternalCommissionStatus.CALCULATED]: 'Calculated',
  [InternalCommissionStatus.APPROVED]: 'Approved',
  [InternalCommissionStatus.PAID]: 'Paid',
  [InternalCommissionStatus.DISPUTED]: 'Disputed',
  [InternalCommissionStatus.VOIDED]: 'Voided',
};

/**
 * Commission receipt status display names
 */
export const COMMISSION_RECEIPT_STATUS_NAMES: Record<CommissionReceiptStatus, string> = {
  [CommissionReceiptStatus.EXPECTED]: 'Expected',
  [CommissionReceiptStatus.RECEIVED]: 'Received',
  [CommissionReceiptStatus.DISPUTED]: 'Disputed',
  [CommissionReceiptStatus.ADJUSTED]: 'Adjusted',
  [CommissionReceiptStatus.WRITTEN_OFF]: 'Written Off',
};

/**
 * Commission split status display names
 */
export const COMMISSION_SPLIT_STATUS_NAMES: Record<CommissionSplitStatus, string> = {
  [CommissionSplitStatus.PENDING]: 'Pending',
  [CommissionSplitStatus.APPROVED]: 'Approved',
  [CommissionSplitStatus.PAID]: 'Paid',
  [CommissionSplitStatus.HELD]: 'On Hold',
};

/**
 * Bonus type display names
 */
export const AGREEMENT_BONUS_TYPE_NAMES: Record<AgreementBonusType, string> = {
  [AgreementBonusType.SIGNING]: 'Signing Bonus',
  [AgreementBonusType.MILESTONE]: 'Milestone Bonus',
  [AgreementBonusType.RETENTION]: 'Retention Bonus',
  [AgreementBonusType.EXCLUSIVITY]: 'Exclusivity Bonus',
  [AgreementBonusType.FIRST_DEAL]: 'First Deal Bonus',
  [AgreementBonusType.PERFORMANCE]: 'Performance Bonus',
};

/**
 * Bonus status display names
 */
export const BONUS_STATUS_NAMES: Record<BonusStatus, string> = {
  [BonusStatus.PENDING]: 'Pending',
  [BonusStatus.EARNED]: 'Earned',
  [BonusStatus.PAID]: 'Paid',
  [BonusStatus.FORFEITED]: 'Forfeited',
};

// ========================================
// EMPLOYEE COMMISSION SPLIT ENUMS (Issue #390)
// ========================================

/**
 * Type of commission payout recipient
 * @see Issue #390 - Employee Commission Split
 */
export enum CommissionPayoutRecipientType {
  EMPLOYEE = 'EMPLOYEE', // Partner's employee who made the referral
  COMPANY = 'COMPANY', // Partner company
  OVERRIDE = 'OVERRIDE', // Special override recipient
  REFERRER = 'REFERRER', // External referrer (not employee)
}

/**
 * Status of a commission payout
 * Each recipient can have a different payout status
 * @see Issue #390 - Employee Commission Split
 */
export enum CommissionPayoutStatus {
  PENDING = 'pending', // Awaiting processing
  APPROVED = 'approved', // Approved for payment
  PROCESSING = 'processing', // Payment in progress
  PAID = 'paid', // Payment completed
  CANCELLED = 'cancelled', // Payout cancelled
  ON_HOLD = 'on_hold', // Temporarily held (compliance, etc.)
}

/**
 * Method for splitting commission between employee and company
 * @see Issue #390 - Employee Commission Split
 */
export enum CommissionSplitMethod {
  PERCENTAGE = 'percentage', // Split by percentage of total commission
  FIXED_EMPLOYEE = 'fixed_employee', // Fixed amount to employee, rest to company
  FIXED_COMPANY = 'fixed_company', // Fixed amount to company, rest to employee
  RATE_BASED = 'rate_based', // Separate rates for employee and company
  TIERED = 'tiered', // Volume-based tiered split
}

/**
 * Type of employee commission override
 * @see Issue #390 - Employee Commission Split
 */
export enum EmployeeOverrideType {
  RATE = 'rate', // Override the commission rate
  PERCENT = 'percent', // Override the split percentage
  FIXED = 'fixed', // Fixed amount per deal
}

/**
 * Payment method for commission payouts
 * @see Issue #390 - Employee Commission Split
 */
export enum CommissionPaymentMethod {
  ACH = 'ACH', // Direct bank transfer
  WIRE = 'WIRE', // Wire transfer
  CHECK = 'CHECK', // Paper check
  PAYROLL = 'PAYROLL', // Through payroll system (for employees)
  DIRECT_DEPOSIT = 'DIRECT_DEPOSIT', // Direct deposit
}

/**
 * Commission payout recipient type display names
 */
export const COMMISSION_PAYOUT_RECIPIENT_TYPE_NAMES: Record<CommissionPayoutRecipientType, string> = {
  [CommissionPayoutRecipientType.EMPLOYEE]: 'Employee',
  [CommissionPayoutRecipientType.COMPANY]: 'Company',
  [CommissionPayoutRecipientType.OVERRIDE]: 'Override',
  [CommissionPayoutRecipientType.REFERRER]: 'Referrer',
};

/**
 * Commission payout status display names
 */
export const COMMISSION_PAYOUT_STATUS_NAMES: Record<CommissionPayoutStatus, string> = {
  [CommissionPayoutStatus.PENDING]: 'Pending',
  [CommissionPayoutStatus.APPROVED]: 'Approved',
  [CommissionPayoutStatus.PROCESSING]: 'Processing',
  [CommissionPayoutStatus.PAID]: 'Paid',
  [CommissionPayoutStatus.CANCELLED]: 'Cancelled',
  [CommissionPayoutStatus.ON_HOLD]: 'On Hold',
};

/**
 * Commission split method display names
 */
export const COMMISSION_SPLIT_METHOD_NAMES: Record<CommissionSplitMethod, string> = {
  [CommissionSplitMethod.PERCENTAGE]: 'Percentage Split',
  [CommissionSplitMethod.FIXED_EMPLOYEE]: 'Fixed Employee Amount',
  [CommissionSplitMethod.FIXED_COMPANY]: 'Fixed Company Amount',
  [CommissionSplitMethod.RATE_BASED]: 'Separate Rates',
  [CommissionSplitMethod.TIERED]: 'Tiered Split',
};

/**
 * Employee override type display names
 */
export const EMPLOYEE_OVERRIDE_TYPE_NAMES: Record<EmployeeOverrideType, string> = {
  [EmployeeOverrideType.RATE]: 'Rate Override',
  [EmployeeOverrideType.PERCENT]: 'Percentage Override',
  [EmployeeOverrideType.FIXED]: 'Fixed Amount',
};

/**
 * Commission payment method display names
 */
export const COMMISSION_PAYMENT_METHOD_NAMES: Record<CommissionPaymentMethod, string> = {
  [CommissionPaymentMethod.ACH]: 'ACH Transfer',
  [CommissionPaymentMethod.WIRE]: 'Wire Transfer',
  [CommissionPaymentMethod.CHECK]: 'Check',
  [CommissionPaymentMethod.PAYROLL]: 'Payroll',
  [CommissionPaymentMethod.DIRECT_DEPOSIT]: 'Direct Deposit',
};
